import { useEffect, useRef, useState } from "react";
import { PhoneCall, PhoneOff, KeyRound, MapPin, Hash } from "lucide-react";
import { Button } from "./ui/button";

function useRingtone(active) {
  const ctxRef = useRef(null);
  const nodesRef = useRef(null);
  useEffect(() => {
    if (!active) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    ctxRef.current = ctx;
    const gain = ctx.createGain();
    gain.gain.value = 0.12;
    gain.connect(ctx.destination);
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    osc1.frequency.value = 440;
    osc2.frequency.value = 480;
    osc1.connect(gain);
    osc2.connect(gain);
    const lfoGain = ctx.createGain();
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 2;
    lfoGain.gain.value = 0.12;
    lfo.connect(lfoGain);
    lfoGain.connect(gain.gain);
    osc1.start(); osc2.start(); lfo.start();
    nodesRef.current = { osc1, osc2, lfo };
    return () => {
      try { osc1.stop(); osc2.stop(); lfo.stop(); } catch (e) {}
      ctx.close();
    };
  }, [active]);
}

export function IncomingCallModal({ call, inCall, onAccept, onReject, onUnlock, onEnd }) {
  const [elapsed, setElapsed] = useState(0);
  useRingtone(!!call && !inCall);

  useEffect(() => {
    if (!inCall) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [inCall]);

  if (!call) return null;
  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-50 backdrop-blur-md bg-black/80 flex items-center justify-center p-4 animate-in fade-in zoom-in-95"
      data-testid="incoming-call-modal">
      <div className="ops-card w-full max-w-md p-8 text-center border-emerald-500/30">
        <div className="relative w-24 h-24 mx-auto mb-6">
          <div className={`absolute inset-0 ${!inCall ? "ring-pulse" : ""}`} />
          <div className={`relative w-24 h-24 rounded-full flex items-center justify-center border-2 ${
            inCall ? "bg-emerald-500/20 border-emerald-400" : "bg-emerald-500/10 border-emerald-500/50"}`}>
            <PhoneCall className="w-10 h-10 text-emerald-400" />
          </div>
        </div>

        <div className="ops-caption mb-1">{inCall ? "In Call" : "Incoming Intercom Call"}</div>
        <h2 className="text-2xl font-bold text-slate-100 mb-2" data-testid="incoming-call-from">{call.from_label}</h2>
        <div className="flex items-center justify-center gap-4 text-sm text-slate-400 mb-8">
          {call.site_name && (
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />{call.site_name}</span>
          )}
          <span className="inline-flex items-center gap-1.5 font-mono"><Hash className="w-3.5 h-3.5" />{call.extension}</span>
        </div>

        {inCall ? (
          <div className="space-y-6">
            <div className="text-4xl font-mono font-bold text-emerald-400" data-testid="call-timer">{mm}:{ss}</div>
            <div className="flex justify-center gap-3">
              <Button data-testid="call-unlock-btn" onClick={onUnlock}
                className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6">
                <KeyRound className="w-4 h-4 mr-2" /> Unlock Door
              </Button>
              <Button data-testid="call-end-btn" onClick={onEnd} variant="destructive" className="px-6">
                <PhoneOff className="w-4 h-4 mr-2" /> End Call
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center gap-3">
            <Button data-testid="incoming-call-reject-btn" onClick={onReject} variant="destructive" className="px-6">
              <PhoneOff className="w-4 h-4 mr-2" /> Reject
            </Button>
            <Button data-testid="incoming-call-unlock-btn" onClick={onUnlock}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-5">
              <KeyRound className="w-4 h-4 mr-2" /> Unlock
            </Button>
            <Button data-testid="incoming-call-accept-btn" onClick={onAccept}
              className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold px-6">
              <PhoneCall className="w-4 h-4 mr-2" /> Accept
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
